import { ChangeEvent, ClipboardEvent, KeyboardEvent, forwardRef } from 'react'

import { formatValue, UnitType } from '../utils/allocation'
import StepButton from './StepButton'

interface AllocationControlProps {
  value: number
  disabled: boolean
  onIncrement: () => void
  onDecrement: () => void
  onChange?: (value: number) => void
  onPaste?: (freeText: string) => void
  stepIncrement: number
  maxValue: number
  minusDisabled: boolean
  plusDisabled: boolean
  ariaLabel: string
  unitType: UnitType
}

const AllocationControl = forwardRef<HTMLInputElement, AllocationControlProps>(
  (
    {
      value,
      disabled,
      onIncrement,
      onDecrement,
      onChange,
      onPaste,
      stepIncrement,
      maxValue,
      minusDisabled,
      plusDisabled,
      ariaLabel,
      unitType,
    },
    ref,
  ) => {
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      // Support arrow keys for increment/decrement
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (!plusDisabled) {
          onIncrement()
        }
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!minusDisabled) {
          onDecrement()
        }
      }
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const parsed = parseInt(event.target.value, 10)
      const clamped = Math.max(
        0,
        Math.min(isNaN(parsed) ? 0 : parsed, maxValue),
      )
      onChange?.(clamped)
    }

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(event.clipboardData.getData('text'))
    }

    return (
      <div className="flex items-center space-x-2">
        <StepButton
          ariaLabel={`Decrease allocation for ${ariaLabel}${stepIncrement !== 1 ? ` by ${formatValue(stepIncrement, unitType)}` : ''}`}
          disabled={disabled || minusDisabled}
          onClick={onDecrement}
          stepIncrement={stepIncrement}
          tabIndex={-1}
          type="minus"
          unitType={unitType}
        />

        <div className="relative flex items-center">
          {unitType === 'dollars' && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-2 text-gray-500"
            >
              $
            </span>
          )}

          <input
            ref={ref}
            aria-disabled={disabled}
            aria-label={`Allocate chips to ${ariaLabel}`}
            className={`h-10 w-20 rounded-lg border-2 border-gray-600 bg-white text-center text-gray-700 shadow-sm transition-colors focus:border-green-2 focus:outline-none focus:ring-2 focus:ring-green-2 focus:ring-opacity-50 disabled:cursor-not-allowed disabled:border-gray-400 disabled:bg-gray-100 disabled:text-gray-400 ${unitType === 'dollars' ? 'pl-5 pr-2' : unitType === 'percentage' ? 'pl-2 pr-5' : 'px-3'}`}
            disabled={disabled}
            max={maxValue}
            min={0}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            step={stepIncrement}
            tabIndex={0}
            type="number"
            value={value}
          />

          {unitType === 'percentage' && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-2 text-gray-500"
            >
              %
            </span>
          )}
        </div>

        <StepButton
          ariaLabel={`Increase allocation for ${ariaLabel}${stepIncrement !== 1 ? ` by ${formatValue(stepIncrement, unitType)}` : ''}`}
          disabled={disabled || plusDisabled}
          onClick={onIncrement}
          stepIncrement={stepIncrement}
          tabIndex={-1}
          type="plus"
          unitType={unitType}
        />
      </div>
    )
  },
)

AllocationControl.displayName = 'AllocationControl'

export default AllocationControl
